import { h, Fragment } from "preact";
import { useState, useEffect, useMemo, useId } from "preact/hooks";
import MonogramAccordion from "./monogram-accordion";
import MonogramPersonalization from "./monogram-personalization";
import MonogramStyleSelector from "./monogram-style-selector";
import MonogramColorSelector from "./monogram-color-selector";
import MonogramPlacementSelector from "./monogram-placement-selector";
import MonogramPreview, {
  prefersAlternateBackground,
} from "./monogram-preview";
import MonogramProductDetail from "./monogram-product-detail";
import {
  availableMonogramTypes,
  findMonogramVariant,
  formatMonogramPrice,
  groupMonogramColors,
  monogramColorContext,
  monogramHeaderLabel,
  resolveMatchingThreadColor,
  restrictLineValues,
  styleSupportsType,
  summarizeSelection,
  transformSelection,
  typeDefaults,
} from "./monogramTypeConfig";

/**
 * Inner content of the monogram modal: the live-preview column, the
 * personalization accordions (type, style, thread color, placement), and the
 * footer with the config-driven copy blocks and the add-to-cart/subtotal row.
 *
 * Content blocks and the disclaimer are read from `window.monogramConfig`,
 * defined by section.monogram.liquid.
 */
export default function MonogramContent({
  onClose,
  onAdd,
  monogram,
  initialSelection,
  currentVariant,
}) {
  const strings = window.localeStrings?.monogram || {};
  const config = window.monogramConfig || {};
  const blocks = config.blocks || [];

  const types = useMemo(
    () => availableMonogramTypes(monogram?.monogramTypes),
    [monogram],
  );

  const styles = useMemo(() => monogram?.monogramStyles || [], [monogram]);

  const placements = useMemo(
    () => monogram?.monogramPlacement || [],
    [monogram],
  );

  const colors = useMemo(() => monogram?.monogramColors || [], [monogram]);

  // Memoised so the colour-default effect below doesn't re-run every render on a
  // fresh context object.
  const colorContext = useMemo(
    () => monogramColorContext(currentVariant),
    [currentVariant],
  );

  const colorGroups = useMemo(
    () =>
      groupMonogramColors(
        colors,
        colorContext.styleColors,
        resolveMatchingThreadColor(colorContext, colors),
      ),
    [colors, colorContext],
  );

  // Canonical display order; its first entry is the default selection and it is
  // the full set of selectable colors.
  const orderedColors = useMemo(
    () => [
      ...colorGroups.matching,
      ...colorGroups.style,
      ...colorGroups.additional,
    ],
    [colorGroups],
  );

  // `values` is keyed by field key (left, middle, …), shared across types where
  // the field name matches. When editing, we seed from the saved selection and
  // the default effects below recover anything no longer offered.
  const [selection, setSelection] = useState(
    initialSelection || {
      type: null,
      values: {},
      styleId: null,
      colorId: null,
      placementId: null,
    },
  );

  const availableStyles = useMemo(
    () =>
      styles.filter((style) =>
        styleSupportsType(style.monogramTypes, selection.type),
      ),
    [styles, selection.type],
  );

  // Default to the first available type, recovering if the current one is no
  // longer offered.
  useEffect(() => {
    if (!types.length) return;
    setSelection((prev) => {
      const type =
        prev.type && types.includes(prev.type) ? prev.type : types[0];
      return {
        ...prev,
        type,
        values: { ...typeDefaults(type, strings.defaults), ...prev.values },
      };
    });
  }, [types]);

  // Default to the first style offered for the current type, recovering if
  // needed.
  useEffect(() => {
    if (!availableStyles.length) return;
    setSelection((prev) => {
      const stillValid = availableStyles.some(
        (style) => style.id === prev.styleId,
      );
      return {
        ...prev,
        styleId: stillValid ? prev.styleId : availableStyles[0].id,
      };
    });
  }, [availableStyles]);

  // Only the matching and style colors come from the variant — the additional
  // ones arrive with `monogram`, which the product form opens the modal without
  // waiting for. Until it lands `orderedColors` is a partial list, and defaulting
  // off it would drop a seeded colorId that is offered after all.
  const colorsLoaded = Boolean(monogram);

  // Default to the first color in display order (matching → style → additional),
  // recovering if the selected one is no longer offered.
  useEffect(() => {
    if (!colorsLoaded || !orderedColors.length) return;
    setSelection((prev) => {
      const stillValid = orderedColors.some(
        (color) => color.id === prev.colorId,
      );
      if (stillValid) return prev;
      return { ...prev, colorId: orderedColors[0].id };
    });
  }, [colorsLoaded, orderedColors]);

  // Default to the first placement, recovering if needed.
  useEffect(() => {
    if (!placements.length) return;
    setSelection((prev) => {
      const stillValid = placements.some(
        (placement) => placement.id === prev.placementId,
      );
      return {
        ...prev,
        placementId: stillValid ? prev.placementId : placements[0].id,
      };
    });
  }, [placements]);

  const productDetailEnabled =
    monogram?.enableProductDetail === "true" ||
    monogram?.enableProductDetail === true;
  const variantImageUrl = currentVariant?.image?.url || null;
  const canShowProductDetail = productDetailEnabled && !!variantImageUrl;

  const [previewMode, setPreviewMode] = useState("monogram");
  const previewToggleName = useId();

  useEffect(() => {
    if (!canShowProductDetail) setPreviewMode("monogram");
  }, [canShowProductDetail]);

  const setType = (type) =>
    setSelection((prev) => ({
      ...prev,
      type,
      values: { ...typeDefaults(type, strings.defaults), ...prev.values },
    }));

  const setValue = (key, value) =>
    setSelection((prev) => ({
      ...prev,
      values: { ...prev.values, [key]: value },
    }));

  const setStyle = (styleId) => setSelection((prev) => ({ ...prev, styleId }));

  const setColor = (colorId) => setSelection((prev) => ({ ...prev, colorId }));

  const setPlacement = (placementId) => {
    setSelection((prev) => ({ ...prev, placementId }));
    if (canShowProductDetail) setPreviewMode("detail");
  };

  const selectedStyle = availableStyles.find(
    (style) => style.id === selection.styleId,
  );

  // Keep the stored line values in sync with the governing style/type (see
  // restrictLineValues).
  useEffect(() => {
    setSelection((prev) => {
      const values = restrictLineValues(prev.type, prev.values, selectedStyle);
      return values === prev.values ? prev : { ...prev, values };
    });
  }, [selection.type, selectedStyle]);

  const selectedColor = orderedColors.find(
    (color) => color.id === selection.colorId,
  );

  const selectedPlacement = placements.find(
    (placement) => placement.id === selection.placementId,
  );

  const personalizationPreview = summarizeSelection(
    selection.type,
    selection.values,
  );

  const personalizationHeader = monogramHeaderLabel(
    selection.type,
    strings.header,
  );

  const stylePreview = selectedStyle
    ? `${strings.style_label || "Style"} ${selectedStyle.styleName}`
    : "";
  const styleImageUrl = selectedStyle?.styleImage?.image?.url;

  const sizeLabel = strings.monogram_size || "Monogram Size";
  const placementPreview = selectedPlacement
    ? `${sizeLabel} ${selectedPlacement.size} / ${selectedPlacement.position}`
    : "";
  const placementImageUrl = selectedPlacement?.image?.image?.url;

  const colorPreview = selectedColor?.label || "";
  const colorImageUrl = selectedColor?.image?.image?.url;

  const pricedVariant = findMonogramVariant(
    monogram?.monogramProduct,
    selectedStyle,
    selectedPlacement,
  );
  const subtotal = formatMonogramPrice(pricedVariant);

  // Commit the configured monogram to the product form. Closing via the header
  // X / "Back to Product" goes through onClose instead and saves nothing.
  const handleAdd = () =>
    onAdd({
      data: transformSelection(selection, {
        style: selectedStyle,
        color: selectedColor,
        placement: selectedPlacement,
      }),
      variant: pricedVariant,
      selection,
      // Kept whole (rather than flattened into `data`) so the form can read the
      // placement's lead time without it reaching the cart line properties.
      placement: selectedPlacement,
    });

  // Pale thread colors read better on the alternate backing — switch to it when
  // it wins on contrast, but only in the monogram view; in the product-detail
  // view the image is the surface.
  const showingDetail = previewMode === "detail" && canShowProductDetail;
  const previewClass =
    !showingDetail && prefersAlternateBackground(selectedColor?.color)
      ? "monogram-modal__preview monogram-modal__preview--low-contrast"
      : "monogram-modal__preview";

  return (
    <Fragment>
      <div className="monogram-modal__body">
        <div className={previewClass}>
          {canShowProductDetail && (
            <div
              className="monogram-modal__preview-toggle"
              role="radiogroup"
              aria-label={strings.preview_mode}
            >
              {[
                ["monogram", strings.monogram],
                ["detail", strings.product_detail],
              ].map(([mode, label]) => (
                <label
                  key={mode}
                  className="monogram-modal__preview-toggle-option"
                >
                  <input
                    type="radio"
                    className="visually-hidden"
                    name={previewToggleName}
                    value={mode}
                    checked={previewMode === mode}
                    onChange={() => setPreviewMode(mode)}
                  />
                  {label}
                </label>
              ))}
            </div>
          )}

          {showingDetail ? (
            <MonogramProductDetail
              image={currentVariant?.image}
              placement={selectedPlacement}
            >
              <MonogramPreview
                type={selection.type}
                values={selection.values}
                style={selectedStyle}
                color={selectedColor?.color}
              />
            </MonogramProductDetail>
          ) : (
            <MonogramPreview
              type={selection.type}
              values={selection.values}
              style={selectedStyle}
              color={selectedColor?.color}
            />
          )}

          {config.disclaimer && (
            <p className="monogram-modal__disclaimer">{config.disclaimer}</p>
          )}
        </div>

        <div className="monogram-modal__steps">
          <MonogramAccordion
            number={1}
            title={strings.personalization}
            preview={personalizationPreview}
            headerImage={
              <span className="monogram-modal__step-label subheading-1">
                {personalizationHeader}
              </span>
            }
          >
            <MonogramPersonalization
              types={types}
              type={selection.type}
              values={selection.values}
              style={selectedStyle}
              onTypeChange={setType}
              onValueChange={setValue}
              strings={strings}
            />
          </MonogramAccordion>

          <MonogramAccordion
            number={2}
            title={strings.monogram_style}
            preview={stylePreview}
            fullBleed
            headerImage={
              styleImageUrl ? (
                <img
                  className="monogram-modal__step-thumb"
                  src={window.getSizedImageUrl(styleImageUrl, "x106")}
                  alt={selectedStyle?.styleName || ""}
                />
              ) : (
                <span className="monogram-modal__step-thumb" />
              )
            }
          >
            <MonogramStyleSelector
              styles={styles}
              type={selection.type}
              selectedId={selection.styleId}
              onSelect={setStyle}
              showMoreLabel={strings.show_more}
            />
          </MonogramAccordion>

          <MonogramAccordion
            number={3}
            title={strings.thread_color}
            preview={colorPreview}
            headerImage={
              selectedColor ? (
                <span
                  className="monogram-color-selector__thumb"
                  style={
                    colorImageUrl
                      ? undefined
                      : { background: selectedColor.color }
                  }
                >
                  {colorImageUrl && (
                    <img
                      src={window.getSizedImageUrl(colorImageUrl, "80x")}
                      alt={selectedColor.label || ""}
                    />
                  )}
                </span>
              ) : (
                <span className="monogram-modal__step-thumb" />
              )
            }
          >
            <MonogramColorSelector
              groups={colorGroups}
              selectedId={selection.colorId}
              onSelect={setColor}
              styleName={colorContext.styleName}
              strings={strings}
              moreColorsCopy={config.moreColorsCopy}
            />
          </MonogramAccordion>

          <MonogramAccordion
            number={4}
            title={strings.placement}
            preview={placementPreview}
            fullBleed
            headerImage={
              placementImageUrl ? (
                <img
                  className="monogram-modal__step-thumb monogram-modal__step-thumb--placement"
                  src={window.getSizedImageUrl(placementImageUrl, "x106")}
                  alt={selectedPlacement?.position || ""}
                />
              ) : (
                <span className="monogram-modal__step-thumb monogram-modal__step-thumb--placement" />
              )
            }
          >
            <MonogramPlacementSelector
              placements={placements}
              selectedId={selection.placementId}
              onSelect={setPlacement}
              sizeLabel={sizeLabel}
              disclaimer={selectedStyle?.disclaimer}
            />
          </MonogramAccordion>

          <footer className="monogram-modal__footer monogram-modal__footer--mobile">
            <div className="monogram-modal__blocks monogram-modal__blocks--desktop">
              {blocks.map((block, index) => (
                <div className="monogram-modal__block" key={index}>
                  {block.heading && (
                    <h3 className="monogram-modal__block-heading">
                      {block.heading}
                    </h3>
                  )}
                  {block.copy && (
                    <div
                      className="monogram-modal__block-copy rte--simple"
                      dangerouslySetInnerHTML={{ __html: block.copy }}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="monogram-modal__checkout">
              <div className="monogram-modal__subtotal">
                <span className="subheading-1">{strings.subtotal}</span>
                <span className="monogram-modal__subtotal-price h6">
                  {subtotal}
                </span>
              </div>
              <button
                type="button"
                className="monogram-modal__add button button--black"
                onClick={handleAdd}
              >
                <span className="utility-3">{strings.add_monogram}</span>
              </button>
            </div>
          </footer>
        </div>
      </div>

      <footer className="monogram-modal__footer monogram-modal__footer--desktop">
        <div className="monogram-modal__blocks monogram-modal__blocks--desktop">
          {blocks.map((block, index) => (
            <div className="monogram-modal__block" key={index}>
              {block.heading && (
                <h3 className="monogram-modal__block-heading">
                  {block.heading}
                </h3>
              )}
              {block.copy && (
                <div
                  className="monogram-modal__block-copy rte--simple"
                  dangerouslySetInnerHTML={{ __html: block.copy }}
                />
              )}
            </div>
          ))}
        </div>

        <div className="monogram-modal__checkout">
          <div className="monogram-modal__subtotal">
            <span className="subheading-1">{strings.subtotal}</span>
            <span className="monogram-modal__subtotal-price h6">
              {subtotal}
            </span>
          </div>
          <button
            type="button"
            className="monogram-modal__add button button--black"
            onClick={handleAdd}
          >
            <span className="utility-3">{strings.add_monogram}</span>
          </button>
        </div>
      </footer>
    </Fragment>
  );
}
